/**
 * Keep-Alive Outlet Component
 * 
 * Renders all open tabs' page components and shows/hides them based on active tab.
 * This prevents re-mounting components when switching tabs.
 * 
 * Features:
 * - Keeps components mounted when switching tabs (no remount)
 * - Preserves scroll position per-tab
 * - Provides isActive context for components to optimize behavior
 */

import { useTabsContext } from '@/context/TabsContext';
import { ActiveTabProvider } from '@/context/ActiveTabContext';
import { RouteParamsContext } from '@/context/RouteParamsContext';
import { TabLocationProvider } from '@/context/TabLocationContext';
import { routes } from '@/routes/routes';
import React, { useMemo, useRef, useEffect, memo, Suspense, lazy, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Spin } from 'antd';
import { TabErrorBoundary } from './TabErrorBoundary';
import './TabsLayout.less';

// Lazy-loaded NotFound component for unmatched routes
const NotFound = lazy(() => import('@/pages/404'));

// Loading fallback for lazy-loaded components
const PageLoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100%',
    width: '100%',
    background: '#f5f7fa',
    minHeight: '200px'
  }}>
    <Spin size="large" />
  </div>
);

// Route matching cache to avoid recomputing for the same paths
const routeCache = new Map<string, { route: typeof routes[0] | null; params: Record<string, string> }>();
const MAX_ROUTE_CACHE_SIZE = 100;

type ScrollSnapshot = {
  element: HTMLElement;
  scrollTop: number;
  scrollLeft: number;
};

type StoredScrollSnapshot = {
  index: number;
  scrollTop: number;
  scrollLeft: number;
};

function captureScrollSnapshot(hostEl: HTMLElement): ScrollSnapshot[] {
  return [hostEl, ...Array.from(hostEl.querySelectorAll<HTMLElement>('*'))]
    .filter((element) => element.scrollTop !== 0 || element.scrollLeft !== 0)
    .map((element) => ({
      element,
      scrollTop: element.scrollTop,
      scrollLeft: element.scrollLeft,
    }));
}

function restoreScrollSnapshot(snapshot: ScrollSnapshot[]): void {
  for (const { element, scrollTop, scrollLeft } of snapshot) {
    element.scrollTop = scrollTop;
    element.scrollLeft = scrollLeft;
  }
}

function captureStoredScrollSnapshot(hostEl: HTMLElement): StoredScrollSnapshot[] {
  return [hostEl, ...Array.from(hostEl.querySelectorAll<HTMLElement>('*'))]
    .map((element, index) => ({
      index,
      scrollTop: element.scrollTop,
      scrollLeft: element.scrollLeft,
    }))
    .filter((entry) => entry.scrollTop !== 0 || entry.scrollLeft !== 0);
}

function restoreStoredScrollSnapshot(hostEl: HTMLElement, snapshot: StoredScrollSnapshot[] | undefined): void {
  if (!snapshot?.length) return;

  const elements = [hostEl, ...Array.from(hostEl.querySelectorAll<HTMLElement>('*'))];
  for (const { index, scrollTop, scrollLeft } of snapshot) {
    const element = elements[index];
    if (!element) continue;
    element.scrollTop = scrollTop;
    element.scrollLeft = scrollLeft;
  }
}

// Match a path to a route and extract params
function matchRoute(path: string): { route: typeof routes[0] | null; params: Record<string, string> } {
  // Normalize path
  const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;

  for (const route of routes) {
    // Handle exact match
    if (route.path === normalizedPath || route.path === path) {
      return { route, params: {} };
    }
    
    // Handle dynamic segments (e.g., /users/:id)
    const routeParts = route.path.split('/');
    const pathParts = normalizedPath.split('/');
    
    if (routeParts.length === pathParts.length) {
      let matches = true;
      const params: Record<string, string> = {};
      
      for (let i = 0; i < routeParts.length; i++) {
        if (routeParts[i].startsWith(':')) {
          // Dynamic segment - extract the param value
          const paramName = routeParts[i].slice(1); // Remove the ':'
          params[paramName] = pathParts[i].split('?')[0];
          continue;
        }
        if (routeParts[i] !== pathParts[i]) {
          matches = false;
          break;
        }
      }
      if (matches) {
        return { route, params };
      }
    }
  }
  
  return { route: null, params: {} };
}

// Cached version of matchRoute to avoid recomputation
function matchRouteCached(path: string): { route: typeof routes[0] | null; params: Record<string, string> } {
  const cached = routeCache.get(path);
  if (cached) return cached;
  
  const result = matchRoute(path);
  routeCache.set(path, result);
  
  // Limit cache size to prevent memory leaks
  if (routeCache.size > MAX_ROUTE_CACHE_SIZE) {
    const firstKey = routeCache.keys().next().value;
    if (firstKey) routeCache.delete(firstKey);
  }
  
  return result;
}

// Deep compare params objects
const paramsEqual = (a: Record<string, string>, b: Record<string, string>): boolean => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  return true;
};

// Memoized page component wrapper with Suspense, error boundary, and custom params/location context
const MemoizedPage = memo<{
  Component: React.ComponentType<any>;
  path: string;
  search: string;
  state: unknown;
  params: Record<string, string>;
  isActive: boolean;
  tabKey: string;
  paneId?: string;
  onCloseTab?: () => void;
}>(({ Component, path, search, state, params, isActive, tabKey, paneId, onCloseTab }) => {
  return (
    <ActiveTabProvider isActive={isActive} tabKey={tabKey} paneId={paneId}>
      <TabLocationProvider pathname={path} search={search} state={state} tabKey={tabKey} paneId={paneId}>
        <RouteParamsContext.Provider value={params}>
          <TabErrorBoundary tabKey={tabKey} path={path} onClose={onCloseTab}>
            <Suspense fallback={<PageLoadingFallback />}>
              <Component />
            </Suspense>
          </TabErrorBoundary>
        </RouteParamsContext.Provider>
      </TabLocationProvider>
    </ActiveTabProvider>
  );
}, (prevProps, nextProps) => {
  return prevProps.Component === nextProps.Component && 
         prevProps.isActive === nextProps.isActive &&
         prevProps.tabKey === nextProps.tabKey &&
         prevProps.paneId === nextProps.paneId &&
         prevProps.path === nextProps.path &&
         prevProps.search === nextProps.search &&
         prevProps.state === nextProps.state &&
         paramsEqual(prevProps.params, nextProps.params);
});

MemoizedPage.displayName = 'MemoizedPage';

/**
 * Keep-Alive Outlet
 * 
 * Instead of using React Router's Outlet, this component:
 * 1. Renders all open tabs' page components
 * 2. Shows only the active tab's component
 * 3. Keeps inactive tabs mounted but hidden
 */
export const KeepAliveOutlet: React.FC = () => {
  const { tabs, activeTabKey, closeTab, config } = useTabsContext();
  return <PaneKeepAliveOutlet tabs={tabs} activeTabKey={activeTabKey} closeTab={closeTab} config={config} isPaneFocused />;
};

type PaneKeepAliveOutletProps = {
  tabs: ReturnType<typeof useTabsContext>['tabs'];
  activeTabKey: string | null;
  closeTab: ReturnType<typeof useTabsContext>['closeTab'];
  config: ReturnType<typeof useTabsContext>['config'];
  isPaneFocused?: boolean;
  paneId?: string;
};

export const PaneKeepAliveOutlet: React.FC<PaneKeepAliveOutletProps> = ({ tabs, activeTabKey, closeTab, config, isPaneFocused = true, paneId }) => {
  const mountedTabsRef = useRef<Set<string>>(new Set());

  // Scroll position preservation
  const scrollPositionsRef = useRef<Map<string, number>>(new Map());
  const contentRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const previousActiveTabRef = useRef<string | null>(null);

  // Create stable close handler for each tab
  const handleCloseTab = useCallback((tabKey: string) => {
    closeTab(tabKey);
    // Clean up scroll position for closed tab
    scrollPositionsRef.current.delete(tabKey);
    contentRefsMap.current.delete(tabKey);
  }, [closeTab]);
  
  // Save scroll position of the previous tab, restore for the new active tab
  useEffect(() => {
    const prevTabKey = previousActiveTabRef.current;

    // Save scroll position of the previous tab
    if (prevTabKey && prevTabKey !== activeTabKey) {
      const prevContentEl = contentRefsMap.current.get(prevTabKey);
      if (prevContentEl) {
        scrollPositionsRef.current.set(prevTabKey, prevContentEl.scrollTop);
      }
    }

    // Restore scroll position for the newly active tab
    if (activeTabKey && activeTabKey !== prevTabKey) {
      // Use requestAnimationFrame to ensure DOM is ready after visibility change
      requestAnimationFrame(() => {
        const contentEl = contentRefsMap.current.get(activeTabKey);
        const savedPosition = scrollPositionsRef.current.get(activeTabKey);
        if (contentEl && savedPosition !== undefined) {
          contentEl.scrollTop = savedPosition;
        }
      });
    }

    previousActiveTabRef.current = activeTabKey;
  }, [activeTabKey]);

  // Track which tabs have been mounted and clean up closed tabs
  useEffect(() => {
    if (activeTabKey) {
      mountedTabsRef.current.add(activeTabKey);
    }
    
    // Clean up keys for tabs that no longer exist (prevents memory leak)
    const currentTabKeys = new Set(tabs.map(t => t.key));
    for (const key of mountedTabsRef.current) {
      if (!currentTabKeys.has(key)) {
        mountedTabsRef.current.delete(key);
        scrollPositionsRef.current.delete(key);
        contentRefsMap.current.delete(key);
      }
    }
  }, [activeTabKey, tabs]);
  
  // Ref callback to store content element references
  const getHostRef = useCallback((key: string) => {
    let hostEl = hostRefsMap.current.get(key);
    if (!hostEl && typeof document !== 'undefined') {
      hostEl = document.createElement('div');
      hostRefsMap.current.set(key, hostEl);
      contentRefsMap.current.set(key, hostEl);
      const scrollListener = () => {
        const currentHostEl = hostRefsMap.current.get(key);
        if (currentHostEl) {
          const snapshot = captureStoredScrollSnapshot(currentHostEl);
          if (snapshot.length > 0) {
            scrollSnapshotsByTabRef.current.set(key, snapshot);
          }
        }
      };
      hostEl.addEventListener('scroll', scrollListener, true);
      scrollListenersByTabRef.current.set(key, scrollListener);
    }
    return hostEl || null;
  }, []);

  useEffect(() => {
    return () => {
      for (const hostEl of hostRefsMap.current.values()) {
        hostEl.remove();
      }
      hostRefsMap.current.clear();
      contentRefsMap.current.clear();
      scrollListenersByTabRef.current.clear();
      scrollSnapshotsByTabRef.current.clear();
    };
  }, []);

  // Extract the current history entry for a tab
  const getTabCurrentEntry = (tab: { key: string; path: string; history: Array<{ path: string; search: string; state?: unknown }>; historyIndex: number }) => {
    const entry = tab.history[tab.historyIndex];
    return {
      path: entry?.path || tab.path,
      search: entry?.search || '',
      state: entry?.state,
    };
  };
  
  // Stable signature including path + search so tab-internal navigation triggers re-render
  const tabsSignature = useMemo(() => 
    tabs.map(t => {
      const entry = t.history[t.historyIndex];
      return `${t.key}:${entry?.path || t.path}:${entry?.search || ''}`;
    }).join('|'),
    [tabs]
  );

  // Build the list of tabs to render (only tabs that have been mounted at least once)
  // Uses cached route matching and stable signature dependency
  // Includes tabs with no matching route to render 404 within the tab
  // Respects enableContentCaching and maxCachedTabs from config
  const tabsToRender = useMemo(() => {
    const result: Array<{
      key: string;
      path: string;
      search: string;
      state: unknown;
      params: Record<string, string>;
      route: typeof routes[0] | null;
      isActive: boolean;
    }> = [];
    
    if (!config.enableContentCaching) {
      // Caching disabled: only render the active tab
      const activeTab = tabs.find(t => t.key === activeTabKey);
      if (activeTab) {
        const { path: currentPath, search, state } = getTabCurrentEntry(activeTab);
        const { route, params } = matchRouteCached(currentPath);
        result.push({
          key: activeTab.key,
          path: currentPath,
          search,
          state,
          params,
          route,
          isActive: true,
        });
      }
      return result;
    }

    // Collect all eligible tabs (mounted before or currently active)
    const eligible: Array<typeof result[0] & { lastAccessed: number }> = [];
    for (const tab of tabs) {
      const { path: currentPath, search, state } = getTabCurrentEntry(tab);
      const { route, params } = matchRouteCached(currentPath);

      if (mountedTabsRef.current.has(tab.key) || tab.key === activeTabKey) {
        eligible.push({
          key: tab.key,
          path: currentPath,
          search,
          state,
          params,
          route,
          isActive: tab.key === activeTabKey,
          lastAccessed: tab.lastAccessed,
        });
      }
    }

    // Enforce maxCachedTabs: keep active tab + most recently accessed inactive tabs
    if (eligible.length > config.maxCachedTabs) {
      const active = eligible.filter(t => t.isActive);
      const inactive = eligible.filter(t => !t.isActive)
        .sort((a, b) => b.lastAccessed - a.lastAccessed)
        .slice(0, config.maxCachedTabs - 1);
      
      // Unmount evicted tabs
      const kept = new Set([...active, ...inactive].map(t => t.key));
      for (const key of mountedTabsRef.current) {
        if (!kept.has(key)) {
          mountedTabsRef.current.delete(key);
          scrollPositionsRef.current.delete(key);
          contentRefsMap.current.delete(key);
        }
      }
      
      return [...active, ...inactive];
    }
    
    return eligible;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsSignature, activeTabKey, config.enableContentCaching, config.maxCachedTabs]);
  
  if (tabsToRender.length === 0) {
    return (
      <div className="tabs-layout-content-container">
        <div className="tabs-layout-content-panel tabs-layout-content-active" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="tabs-layout-content-container">
      {tabsToRender.map(({ key, path, search, state, params, route, isActive }) => {
        const Component = route?.component ?? NotFound;
        
        return (
          <div
            key={key}
            ref={(el) => setContentRef(key, el)}
            className={`tabs-layout-content-panel ${isActive ? 'tabs-layout-content-active' : 'tabs-layout-content-hidden'}`}
            data-testid="tabs-layout-content-panel"
            data-tab-key={key}
          >
            <MemoizedPage 
              Component={Component}
              path={path}
              search={search}
              state={state}
              params={params}
              isActive={isActive && isPaneFocused}
              tabKey={key}
              paneId={paneId}
              onCloseTab={() => handleCloseTab(key)}
            />
          </div>
        );
      })}
    </div>
  );
};

type PortalKeepAliveOutletProps = {
  tabs: ReturnType<typeof useTabsContext>['tabs'];
  panes: ReturnType<typeof useTabsContext>['panes'];
  closeTab: ReturnType<typeof useTabsContext>['closeTab'];
  config: ReturnType<typeof useTabsContext>['config'];
  paneContainers: Map<string, HTMLDivElement>;
  paneContainerVersion: number;
};

export const PortalKeepAliveOutlet: React.FC<PortalKeepAliveOutletProps> = ({
  tabs,
  panes,
  closeTab,
  config,
  paneContainers,
  paneContainerVersion,
}) => {
  const mountedTabsRef = useRef<Set<string>>(new Set());
  const scrollPositionsRef = useRef<Map<string, number>>(new Map());
  const contentRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const hostRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollSnapshotsByTabRef = useRef<Map<string, StoredScrollSnapshot[]>>(new Map());
  const scrollListenersByTabRef = useRef<Map<string, EventListener>>(new Map());
  const scrollElementCleanupsByTabRef = useRef<Map<string, Array<() => void>>>(new Map());
  const previousActiveByPaneRef = useRef<Map<string, string | null>>(new Map());

  const rememberScrollSnapshot = useCallback((key: string) => {
    const currentHostEl = hostRefsMap.current.get(key);
    if (!currentHostEl) return;

    const snapshot = captureStoredScrollSnapshot(currentHostEl);
    if (snapshot.length > 0) {
      scrollSnapshotsByTabRef.current.set(key, snapshot);
    }
  }, []);

  const refreshScrollElementListeners = useCallback((key: string, hostEl: HTMLElement) => {
    for (const cleanup of scrollElementCleanupsByTabRef.current.get(key) || []) {
      cleanup();
    }

    const listener = () => rememberScrollSnapshot(key);
    const elements = [hostEl, ...Array.from(hostEl.querySelectorAll<HTMLElement>('*'))];
    for (const element of elements) {
      element.addEventListener('scroll', listener);
    }
    scrollElementCleanupsByTabRef.current.set(
      key,
      elements.map((element) => () => element.removeEventListener('scroll', listener))
    );
  }, [rememberScrollSnapshot]);

  const activeTabKeys = useMemo(() => new Set(panes.map((pane) => pane.activeTabKey).filter(Boolean) as string[]), [panes]);
  const tabPaneByKey = useMemo(() => {
    const result = new Map<string, typeof panes[number]>();
    for (const pane of panes) {
      for (const tabKey of pane.tabKeys) {
        if (!result.has(tabKey)) {
          result.set(tabKey, pane);
        }
      }
    }
    return result;
  }, [panes]);

  const handleCloseTab = useCallback((tabKey: string) => {
    closeTab(tabKey);
    scrollPositionsRef.current.delete(tabKey);
    contentRefsMap.current.delete(tabKey);
    const hostEl = hostRefsMap.current.get(tabKey);
    const scrollListener = scrollListenersByTabRef.current.get(tabKey);
    if (hostEl && scrollListener) {
      hostEl.removeEventListener('scroll', scrollListener, true);
    }
    for (const cleanup of scrollElementCleanupsByTabRef.current.get(tabKey) || []) {
      cleanup();
    }
    hostEl?.remove();
    hostRefsMap.current.delete(tabKey);
    scrollListenersByTabRef.current.delete(tabKey);
    scrollElementCleanupsByTabRef.current.delete(tabKey);
    scrollSnapshotsByTabRef.current.delete(tabKey);
  }, [closeTab]);

  useEffect(() => {
    for (const key of activeTabKeys) {
      mountedTabsRef.current.add(key);
    }

    const currentTabKeys = new Set(tabs.map((tab) => tab.key));
    for (const key of mountedTabsRef.current) {
      if (!currentTabKeys.has(key)) {
        mountedTabsRef.current.delete(key);
        scrollPositionsRef.current.delete(key);
        contentRefsMap.current.delete(key);
        const hostEl = hostRefsMap.current.get(key);
        const scrollListener = scrollListenersByTabRef.current.get(key);
        if (hostEl && scrollListener) {
          hostEl.removeEventListener('scroll', scrollListener, true);
        }
        for (const cleanup of scrollElementCleanupsByTabRef.current.get(key) || []) {
          cleanup();
        }
        hostEl?.remove();
        hostRefsMap.current.delete(key);
        scrollListenersByTabRef.current.delete(key);
        scrollElementCleanupsByTabRef.current.delete(key);
        scrollSnapshotsByTabRef.current.delete(key);
      }
    }
  }, [activeTabKeys, tabs]);

  useEffect(() => {
    for (const pane of panes) {
      const prevTabKey = previousActiveByPaneRef.current.get(pane.id) || null;
      const activeTabKey = pane.activeTabKey;

      if (prevTabKey && prevTabKey !== activeTabKey) {
        const prevContentEl = contentRefsMap.current.get(prevTabKey);
        if (prevContentEl) {
          scrollPositionsRef.current.set(prevTabKey, prevContentEl.scrollTop);
        }
      }

      if (activeTabKey && activeTabKey !== prevTabKey) {
        requestAnimationFrame(() => {
          const contentEl = contentRefsMap.current.get(activeTabKey);
          const savedPosition = scrollPositionsRef.current.get(activeTabKey);
          if (contentEl && savedPosition !== undefined) {
            contentEl.scrollTop = savedPosition;
          }
        });
      }

      previousActiveByPaneRef.current.set(pane.id, activeTabKey);
    }
  }, [panes]);

  const getHostRef = useCallback((key: string) => {
    let hostEl = hostRefsMap.current.get(key);
    if (!hostEl && typeof document !== 'undefined') {
      hostEl = document.createElement('div');
      hostRefsMap.current.set(key, hostEl);
      contentRefsMap.current.set(key, hostEl);
      const scrollListener = () => rememberScrollSnapshot(key);
      hostEl.addEventListener('scroll', scrollListener, true);
      scrollListenersByTabRef.current.set(key, scrollListener);
    }
    return hostEl || null;
  }, [rememberScrollSnapshot]);

  useEffect(() => {
    return () => {
      for (const hostEl of hostRefsMap.current.values()) {
        hostEl.remove();
      }
      hostRefsMap.current.clear();
      contentRefsMap.current.clear();
      scrollListenersByTabRef.current.clear();
      for (const cleanups of scrollElementCleanupsByTabRef.current.values()) {
        for (const cleanup of cleanups) {
          cleanup();
        }
      }
      scrollElementCleanupsByTabRef.current.clear();
      scrollSnapshotsByTabRef.current.clear();
    };
  }, []);

  const getTabCurrentEntry = (tab: { key: string; path: string; history: Array<{ path: string; search: string; state?: unknown }>; historyIndex: number }) => {
    const entry = tab.history[tab.historyIndex];
    return {
      path: entry?.path || tab.path,
      search: entry?.search || '',
      state: entry?.state,
    };
  };

  const tabsSignature = useMemo(() =>
    tabs.map((tab) => {
      const entry = tab.history[tab.historyIndex];
      const pane = tabPaneByKey.get(tab.key);
      return `${tab.key}:${pane?.id || ''}:${entry?.path || tab.path}:${entry?.search || ''}:${pane?.activeTabKey === tab.key ? '1' : '0'}`;
    }).join('|'),
    [tabs, tabPaneByKey]
  );

  const tabsToRender = useMemo(() => {
    const eligible: Array<{
      key: string;
      path: string;
      search: string;
      state: unknown;
      params: Record<string, string>;
      route: typeof routes[0] | null;
      isActive: boolean;
      paneId: string;
      lastAccessed: number;
    }> = [];

    for (const tab of tabs) {
      const pane = tabPaneByKey.get(tab.key);
      if (!pane) continue;
      const isActive = pane.activeTabKey === tab.key;
      if (!config.enableContentCaching && !isActive) continue;
      if (!mountedTabsRef.current.has(tab.key) && !isActive) continue;

      const { path: currentPath, search, state } = getTabCurrentEntry(tab);
      const { route, params } = matchRouteCached(currentPath);
      eligible.push({
        key: tab.key,
        path: currentPath,
        search,
        state,
        params,
        route,
        isActive,
        paneId: pane.id,
        lastAccessed: tab.lastAccessed,
      });
    }

    if (config.enableContentCaching && eligible.length > config.maxCachedTabs) {
      const active = eligible.filter((tab) => tab.isActive);
      const inactive = eligible
        .filter((tab) => !tab.isActive)
        .sort((a, b) => b.lastAccessed - a.lastAccessed)
        .slice(0, Math.max(0, config.maxCachedTabs - active.length));
      const kept = new Set([...active, ...inactive].map((tab) => tab.key));

      for (const key of mountedTabsRef.current) {
        if (!kept.has(key)) {
          mountedTabsRef.current.delete(key);
          scrollPositionsRef.current.delete(key);
          contentRefsMap.current.delete(key);
          const hostEl = hostRefsMap.current.get(key);
          const scrollListener = scrollListenersByTabRef.current.get(key);
          if (hostEl && scrollListener) {
            hostEl.removeEventListener('scroll', scrollListener, true);
          }
          for (const cleanup of scrollElementCleanupsByTabRef.current.get(key) || []) {
            cleanup();
          }
          hostEl?.remove();
          hostRefsMap.current.delete(key);
          scrollListenersByTabRef.current.delete(key);
          scrollElementCleanupsByTabRef.current.delete(key);
          scrollSnapshotsByTabRef.current.delete(key);
        }
      }

      return [...active, ...inactive];
    }

    return eligible;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsSignature, config.enableContentCaching, config.maxCachedTabs]);

  useLayoutEffect(() => {
    const renderedKeys = new Set(tabsToRender.map((tab) => tab.key));

    for (const { key, isActive, paneId } of tabsToRender) {
      const container = paneContainers.get(paneId);
      const hostEl = getHostRef(key);
      if (!container || !hostEl) continue;

      hostEl.className = `tabs-layout-content-panel ${isActive ? 'tabs-layout-content-active' : 'tabs-layout-content-hidden'}`;
      hostEl.dataset.testid = 'tabs-layout-content-panel';
      hostEl.dataset.tabKey = key;
      refreshScrollElementListeners(key, hostEl);
      setTimeout(() => refreshScrollElementListeners(key, hostEl), 0);

      if (hostEl.parentElement !== container) {
        const scrollSnapshot = captureScrollSnapshot(hostEl);
        const storedScrollSnapshot = scrollSnapshotsByTabRef.current.get(key);
        container.appendChild(hostEl);
        restoreScrollSnapshot(scrollSnapshot);
        restoreStoredScrollSnapshot(hostEl, storedScrollSnapshot);
        if (storedScrollSnapshot?.length) {
          scrollSnapshotsByTabRef.current.set(key, storedScrollSnapshot);
        }
        requestAnimationFrame(() => {
          restoreScrollSnapshot(scrollSnapshot);
          restoreStoredScrollSnapshot(hostEl, storedScrollSnapshot);
          if (storedScrollSnapshot?.length) {
            scrollSnapshotsByTabRef.current.set(key, storedScrollSnapshot);
          }
        });
      }
    }

    for (const [key, hostEl] of hostRefsMap.current) {
      if (!renderedKeys.has(key)) {
        const scrollListener = scrollListenersByTabRef.current.get(key);
        if (scrollListener) {
          hostEl.removeEventListener('scroll', scrollListener, true);
        }
        for (const cleanup of scrollElementCleanupsByTabRef.current.get(key) || []) {
          cleanup();
        }
        hostEl.remove();
        hostRefsMap.current.delete(key);
        contentRefsMap.current.delete(key);
        scrollListenersByTabRef.current.delete(key);
        scrollElementCleanupsByTabRef.current.delete(key);
        scrollSnapshotsByTabRef.current.delete(key);
      }
    }
  }, [getHostRef, paneContainerVersion, paneContainers, refreshScrollElementListeners, tabsToRender]);

  return (
    <>
      {tabsToRender.map(({ key, path, search, state, params, route, isActive, paneId }) => {
        const hostEl = getHostRef(key);
        if (!hostEl) return null;

        const Component = route?.component ?? NotFound;
        return createPortal(
          <MemoizedPage
            Component={Component}
            path={path}
            search={search}
            state={state}
            params={params}
            isActive={isActive}
            tabKey={key}
            paneId={paneId}
            onCloseTab={() => handleCloseTab(key)}
          />,
          hostEl,
          key
        );
      })}
    </>
  );
};

export default KeepAliveOutlet;
